import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { StepCompProps, StepCompRef } from "@layouts/stepper";
import { DisqualifyAllProductsDialogProps } from "@models/platform-analysis/entities/analysis";
import { ProductsShortlistValidationStatus } from "@models/platform-analysis/enums/status";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { ShortlistedProductsExceededLimitDialog } from "@pages/platform-analysis-v2/_id/components/dialogs/shortlisted-exceeded-limit";
import { AnalysisVirtualizedTable } from "@pages/platform-analysis-v2/_id/components/virtualized-table";
import { AnalysisVirtualizedTableStepConfigs } from "@pages/platform-analysis-v2/_id/configs";
import {
  checkStepIsLastVisibleStep,
  validateReviewSelectedProductsStatuses,
} from "@pages/platform-analysis-v2/_id/utils";
import { RootState } from "@redux/store";
import { ForwardRefRenderFunction, useImperativeHandle, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { useEffectOnce, useToggle } from "react-use";
import { HasUndecidedPlatformDialog } from "../../../components/dialogs/has-undecided-product";
import { NoProductShortlistedDialog } from "../../../components/dialogs/no-shortlisted-product";
import { FeatureAnalysisSkeleton } from "../../../components/skeletons/feature-analysis";

const TABLE_VIRTUALIZE_PROPS =
  AnalysisVirtualizedTableStepConfigs[AnalysisStep.Feature];

export const FeatureAnalysisStep: ForwardRefRenderFunction<
  StepCompRef,
  StepCompProps
> = ({ stepNumber }, ref) => {
  const { analysisId } = useParams<{ analysisId: string }>();

  const storedIsLoading = useSelector(
    (state: RootState) => state.platformAnalysis.ui.isLoading,
  );
  const storedSteps = useSelector(
    (state: RootState) => state.platformAnalysis?.ui?.steps,
  );

  const {
    isCompleted,
    isReadOnly,
    platformAnalysis,
    isShowAnalysisScores,
    loadPlatformAnalysis,
    disqualifyAllPlatforms,
    saveAnalysisFilters,
    updateFinalJSONs,
  } = usePlatformAnalysis();

  // #region States & Values
  const [isLoading, toggleIsLoading] = useToggle(true);
  const [showNoShortlistedDialog, toggleShowNoShortlistedDialog] =
    useToggle(false);
  const [isSubmittingDisqualifyDialog, toggleSubmittingDisqualifyDialog] =
    useToggle(false);
  const [
    showShortlistedExceededLimitDialog,
    toggleShowShortlistedExceededLimitDialog,
  ] = useToggle(false);
  const [disqualifyAllDialog, setDisqualifyAllDialog] =
    useState<DisqualifyAllProductsDialogProps>();
  // #endregion States & Values

  // #region Handlers
  useImperativeHandle(ref, () => ({
    onChangeStep: async (_, isNext) => {
      if (!isNext) return true;
      return await handleOnSubmit();
    },
    onChangeOtherStep: async () => {
      return await handleOnSubmit();
    },
    onSubmit: async () => {
      return await handleOnSubmit();
    },
  }));

  const getDisqualifyAllPlatform = async (
    numOfShortlistedProducts: number,
    numOfUndecidedProducts: number,
    numOfDisqualifiedProducts: number,
  ): Promise<boolean> => {
    return await new Promise((res) => {
      setDisqualifyAllDialog({
        callback: res,
        numOfShortlistedProducts,
        numOfUndecidedProducts,
        numOfDisqualifiedProducts,
      });
    });
  };

  const handleOnDisqualifyAllPlatforms = async () => {
    if (!analysisId) return false;
    const isSuccess = await disqualifyAllPlatforms({
      analysisId,
      analysisStep: AnalysisStep.Feature,
    });
    return !!isSuccess;
  };

  const validateAndHandleReviewProducts = async (
    isLastVisibleStep: boolean,
  ): Promise<boolean> => {
    const selectedProducts =
      platformAnalysis?.feature?.all?.selectedProducts || [];

    const { status, numOfShortlisted, numOfDisqualified, numOfUndecided } =
      validateReviewSelectedProductsStatuses({
        selectedProducts,
        isLastVisibleStep,
      });

    switch (status) {
      case ProductsShortlistValidationStatus.NoShortlisted:
        toggleShowNoShortlistedDialog(true);
        return false;

      case ProductsShortlistValidationStatus.HasUndecided:
        return await getDisqualifyAllPlatform(
          numOfShortlisted,
          numOfUndecided,
          numOfDisqualified,
        );

      case ProductsShortlistValidationStatus.ShortlistLimitExceeded:
        toggleShowShortlistedExceededLimitDialog(true);
        return false;

      default:
        return true;
    }
  };

  const handleOnSubmit = async () => {
    if (!analysisId || analysisId === "new") return false;
    if (isCompleted || isReadOnly) return true;

    await saveAnalysisFilters(analysisId, {
      showAnalysisScore: isShowAnalysisScores,
    });

    const isLastVisibleStep = checkStepIsLastVisibleStep({
      analysisSteps: storedSteps,
      stepToCheck: AnalysisStep.Feature,
    });

    // Proceed if not in review mode
    if (!platformAnalysis?.isReview) {
      if (isLastVisibleStep) await updateFinalJSONs(analysisId);
      return true;
    }

    // Validate review
    const isValid = await validateAndHandleReviewProducts(isLastVisibleStep);
    if (isValid && isLastVisibleStep) await updateFinalJSONs(analysisId);
    return isValid;
  };
  // #endregion Handlers

  // #region Effects & Variables
  useEffectOnce(() => {
    if (!analysisId) return;
    loadPlatformAnalysis({
      platformAnalysisId: analysisId,
      stepId: AnalysisStep.Feature,
    }).then((analysis) => {
      toggleIsLoading(!analysis);
    });
  });
  // #endregion Effects & Variables

  if (isLoading || storedIsLoading || !TABLE_VIRTUALIZE_PROPS)
    return <FeatureAnalysisSkeleton />;

  return (
    <>
      <AnalysisVirtualizedTable
        disabled={isReadOnly || isCompleted}
        stepNumber={stepNumber ?? 0}
        virtualizedProps={TABLE_VIRTUALIZE_PROPS}
      />

      <NoProductShortlistedDialog
        open={showNoShortlistedDialog}
        onClose={() => toggleShowNoShortlistedDialog(false)}
      />

      <ShortlistedProductsExceededLimitDialog
        open={showShortlistedExceededLimitDialog}
        onClose={() => toggleShowShortlistedExceededLimitDialog(false)}
      />

      <HasUndecidedPlatformDialog
        disqualifyProps={disqualifyAllDialog}
        isSubmitting={isSubmittingDisqualifyDialog}
        onClose={() => {
          disqualifyAllDialog?.callback(false);
          setDisqualifyAllDialog(undefined);
        }}
        onSubmit={async () => {
          toggleSubmittingDisqualifyDialog(true);
          handleOnDisqualifyAllPlatforms().then((res) => {
            disqualifyAllDialog?.callback(res);
            toggleSubmittingDisqualifyDialog(false);

            if (!res) return;
            setDisqualifyAllDialog(undefined);
          });
        }}
      />
    </>
  );
};
